import React from "react";
//#region types
import { BaseFormFieldProps, WintFormFieldProps } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import FormFieldLayout from "./formFieldLayout";
import Description from "./description";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

export type FormFieldProps<T extends BaseFormFieldProps> = WintFormFieldProps & T;

function FormField<T extends BaseFormFieldProps>(props: FormFieldProps<T>) {
  const {
    className,
    styles,
    direction,
    autogeneratedText,
    hideMainLabel,
    Component,
    name,
    value,
    onChange,
    onBlur,
    setValue,
    setTouched,
    touched,
    error,
    hasError,
    label,
    controlLabel,
    descriptionText,
    fieldValueDynamicDescriptionText,
    customMessage,
    onContainerFocus: callerOnContainerFocus,
    onContainerBlur: callerOnContainerBlur,
    children,
    ...componentProps
  } = props;

  const actualLabel = typeof label === "string" ? LocalizationHelperInstance.getFormattedMessage(label, label) : label;

  // Stay calm while the field is actively focused: suppress the invalid/red state, error icon
  // and message bubble whilst the user is in the field, returning them on blur if still invalid.
  // One focus-aware value feeds both the field styling and the layout's message bubble.
  const [focused, setFocused] = React.useState(false);
  const effectiveHasError = focused ? false : hasError;

  // Compose internal focus tracking with any caller-provided container handlers.
  const onContainerFocus = (event: React.FocusEvent) => {
    setFocused(true);
    callerOnContainerFocus?.(event);
  };
  const onContainerBlur = (event: React.FocusEvent) => {
    // Ignore focus moves within the field (e.g. input <-> clear button) to avoid flicker.
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
    callerOnContainerBlur?.(event);
  };

  return (
    <FormFieldLayout
      {...props}
      hasError={effectiveHasError}
      onContainerFocus={onContainerFocus}
      onContainerBlur={onContainerBlur}
    >
      <Component
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        setValue={setValue}
        setTouched={setTouched}
        touched={touched}
        error={error}
        hasError={effectiveHasError}
        label={actualLabel}
        {...componentProps}
      />
      {/* Rendered right after the field, before any trailing children (e.g. a suggestions row) —
          feedback about the field's own value belongs closer to it than unrelated trailing content. */}
      {fieldValueDynamicDescriptionText && (
        <Description className={cssExports.noWrap}>{fieldValueDynamicDescriptionText}</Description>
      )}
      {children}
    </FormFieldLayout>
  );
}

export default FormField;
